import { useMemo, useState } from "react";
import { Anchor, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { HintTooltip } from "./HintTooltip";
import { AthletePlanningCalendar } from "../planning/AthletePlanningCalendar";
import { parseLocalDate } from "../../lib/programBuilderUtils";

/**
 * "Agenda" tenant of the builder dock (docs/program-builder-season-strip,
 * Fase 1b): the athlete's planning calendar next to the editor, one month at
 * a time, with the week open in the editor highlighted so the coach sees where
 * a phase falls while drawing it. Writes go through the shared calendar
 * component; the parent refetches the season agenda, which refreshes both the
 * strip and this panel.
 */
export function SeasonAgendaPanel({
	// `scope` is `{ kind: "athlete" | "group", id }`; options share the
	// `{ user_id, name }` shape whichever kind they are.
	scope,
	subjectOptions,
	onSelectSubject,
	memberAthleteIds,
	mesocycles,
	competitions,
	loading,
	onChanged,
	selectedWeekRange,
	t,
}) {
	// Same default as the reference panel: the dock follows the editor until
	// the coach pins it.
	const [followEditor, setFollowEditor] = useState(true);

	const focusMonth = useMemo(() => {
		if (!followEditor || !selectedWeekRange?.start) return undefined;
		const date = parseLocalDate(selectedWeekRange.start);
		return { year: date.getFullYear(), month: date.getMonth() };
	}, [followEditor, selectedWeekRange?.start]);

	const isGroup = scope?.kind === "group";
	const subjectId = scope?.id || "";
	const subjectName =
		(subjectOptions || []).find((item) => item.user_id === subjectId)?.name ||
		"";

	return (
		<div className="flex flex-col h-full min-h-0" data-testid="season-agenda-panel">
			<div className="px-2 py-2 border-b border-border shrink-0 flex items-center gap-1.5">
				{(subjectOptions || []).length > 1 ? (
					<Select value={subjectId} onValueChange={onSelectSubject}>
						<SelectTrigger
							className="h-8 flex-1 min-w-0 rounded-sm border-border bg-transparent text-xs"
							aria-label={t("pbSeasonAthleteSelector")}
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							{subjectOptions.map((athlete) => (
								<SelectItem key={athlete.user_id} value={athlete.user_id}>
									{athlete.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				) : (
					<span className="flex-1 min-w-0 truncate text-xs font-heading uppercase tracking-wider text-muted-foreground px-1">
						{subjectName}
					</span>
				)}
				<HintTooltip
					label={
						followEditor
							? t("pbSeasonFollowEditorOn")
							: t("pbSeasonFollowEditorOff")
					}
				>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={() => setFollowEditor((prev) => !prev)}
						aria-pressed={followEditor}
						aria-label={
							followEditor
								? t("pbSeasonFollowEditorOn")
								: t("pbSeasonFollowEditorOff")
						}
						className={`h-8 w-8 shrink-0 rounded-sm ${
							followEditor
								? "text-volt bg-volt/10"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						<Anchor className="w-3.5 h-3.5" />
					</Button>
				</HintTooltip>
				{/* Groups have no page of their own yet: the dock is where they
				    are planned. */}
				{!isGroup && (
					<HintTooltip label={t("pbSeasonOpenAthlete")}>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							onClick={() =>
								window.open(
									`/athletes/${subjectId}?tab=planning`,
									"_blank",
									"noopener,noreferrer",
								)
							}
							aria-label={t("pbSeasonOpenAthlete")}
							className="h-8 w-8 shrink-0 rounded-sm text-muted-foreground hover:text-volt"
							disabled={!subjectId}
						>
							<ExternalLink className="w-3.5 h-3.5" />
						</Button>
					</HintTooltip>
				)}
			</div>
			<div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-3">
				<p className="text-[11px] text-muted-foreground">
					{t("pbSeasonAgendaHint")}
				</p>
				<AthletePlanningCalendar
					key={`${scope?.kind}-${subjectId}`}
					athleteId={isGroup ? undefined : subjectId}
					groupId={isGroup ? subjectId : undefined}
					memberAthleteIds={isGroup ? memberAthleteIds : undefined}
					mesocycles={mesocycles}
					competitions={competitions}
					loading={loading}
					onMesocyclesChanged={onChanged}
					onCompetitionsChanged={onChanged}
					monthsSelectable={false}
					defaultMonthsToShow={1}
					focusMonth={focusMonth}
					highlightRange={selectedWeekRange}
					compact
				/>
			</div>
		</div>
	);
}
